/*eslint  @typescript-eslint/no-use-before-define: 0*/

import _ from "lodash";
import { DataOptions } from "vuetify";
import { defineModule } from "direct-vuex";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  addToDocumentNegativeCostDocumentPair,
  addToDocumentPositiveCostDocumentPair,
  changePurchasePrice,
  deleteDocument,
  deletePairCostDocument,
  generateVirtualSaleDocument,
  getSettlementDocumentData,
  getUnsettledDocumentItems,
  getVATMarginDocuments,
  postSendSettlement,
  postSettleDocumentItems,
} from "@/requests/settlement";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { VATMarginDocument } from "@/models/api/settlements/VATMarginDocument";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { UnsettledDocumentItem } from "@/models/api/settlements/UsettledDocumentItem";
import { SettlementData } from "@/models/api/settlements/SettlementData";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";
import { bold } from "@/filters/bold";

export interface VATMarginSettlementsStateInterface {
  documentFilters: Array<CriterionItemRequest> | null;
  documentTableData: Array<VATMarginDocument>;
  documentTableOptions: DataOptions;
  documentTableCount: number;
  recordStampId: number | null;
  isLoading: boolean;
  documentId: string | null;
  unsettledDocumentItems: Array<UnsettledDocumentItem>;
  settlementDocumentId: string | null;
  settlementDocumentData: Array<SettlementData>;
}

export const VATMarginSettlementsState: VATMarginSettlementsStateInterface = {
  documentFilters: null,
  documentTableData: [],
  documentTableOptions: new DefaultDataOptions(),
  documentTableCount: -1,
  recordStampId: null,
  isLoading: true,
  documentId: null,
  unsettledDocumentItems: [],
  settlementDocumentId: null,
  settlementDocumentData: [],
};

const VATMarginSettlementsModule = defineModule({
  state: VATMarginSettlementsState as VATMarginSettlementsStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setDocumentFilters(state, param: Array<CriterionItemRequest> | null) {
      state.documentFilters = param;
    },
    setDocumentTableData(state, param: Array<VATMarginDocument>) {
      state.documentTableData = param;
    },
    setDocumentTableOptions(state, param: DataOptions) {
      state.documentTableOptions = param;
    },
    setDocumentTableCount(state, param: number) {
      state.documentTableCount = param;
    },
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setDocumentId(state, param: string | null) {
      state.documentId = param;
    },
    setUnsettledDocumentItems(state, param: Array<UnsettledDocumentItem>) {
      state.unsettledDocumentItems = param;
    },
    setSettlementDocumentId(state, param: string | null) {
      state.settlementDocumentId = param;
    },
    setSettlementDocumentData(state, param: Array<SettlementData>) {
      state.settlementDocumentData = param;
    },
  },
  actions: {
    async downloadDocuments(context) {
      const { commit, state } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getVATMarginDocuments({
        count: state.documentTableOptions.itemsPerPage,
        page: state.documentTableOptions.page - 1,
        criterions: _.cloneDeep(state.documentFilters),
        filterName: FILTER_NAMES.SettlementStandardFilter,
      });
      commit.setDocumentTableData(response.items);
      commit.setDocumentTableCount(response.totalCount);
      commit.setIsLoading(false);
    },
    async sendSettlement(context, documentId: string) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await postSendSettlement(documentId);
      await dispatch.downloadDocuments();
      await rootDispatch.popup.addSavedOkSnackbar();
    },
    async downloadUnsettledDocumentItems(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.documentId) {
        const response = await getUnsettledDocumentItems(state.documentId);
        commit.setUnsettledDocumentItems(response);
      }
    },
    async settleDocumentItems(context) {
      const { state, commit, dispatch } = _moduleActionContext(context);
      if (state.documentId) {
        await postSettleDocumentItems(state.documentId);
        await dispatch.downloadDocuments();
        commit.setDocumentId(null);
      }
    },
    async downloadSettlementDocumentData(context) {
      const { state, commit } = _moduleActionContext(context);
      if (state.settlementDocumentId) {
        const response = await getSettlementDocumentData(state.settlementDocumentId);
        commit.setSettlementDocumentData(response);
      }
    },
    async deleteDocument(context, documentId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);

      const document = state.documentTableData.find((x) => x.id == documentId);
      const content = filterCapitalize(
        i18n
          .t("areYouSureToDeleteDocument", {
            documentNumber: bold(document?.documentNumber),
          })
          .toString()
      );

      const confirm = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(content, filterCapitalize(i18n.t("warning").toString()))
      )) as boolean;
      if (confirm) {
        const result = await deleteDocument(documentId, false);
        await DoubleConfirm.Process(result, rootDispatch.popup.addConfirm, async () => {
          await deleteDocument(documentId, true);
        });
      }
      dispatch.downloadDocuments();
      await rootDispatch.popup.addSavedOkSnackbar();
    },
    async deletePairCostDocument(context, documentId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);

      const document = state.documentTableData.find((x) => x.id == documentId);
      const content = filterCapitalize(
        i18n
          .t("areYouSureToDeletePairWithCostDocuments", {
            documentNumber: bold(document?.documentNumber),
          })
          .toString()
      );

      const confirm = (await rootDispatch.popup.addConfirm(
        new ConfirmModel(content, filterCapitalize(i18n.t("warning").toString()))
      )) as boolean;
      if (confirm) {
        const result = await deletePairCostDocument(documentId, false);
        await DoubleConfirm.Process(result, rootDispatch.popup.addConfirm, async () => {
          await deletePairCostDocument(documentId, true);
        });
      }
      dispatch.downloadDocuments();
      await rootDispatch.popup.addSavedOkSnackbar();
    },
    async changePurchasePrice(context, param: UnsettledDocumentItem) {
      const { dispatch } = _moduleActionContext(context);
      if (param.purchasePrice != null) {
        await changePurchasePrice(param.id, param.purchasePrice).catch(
          async () => await dispatch.downloadUnsettledDocumentItems()
        );
      }
    },
    async generateVirtualSaleDocument(context, documentId: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await generateVirtualSaleDocument(documentId);
      await dispatch.downloadDocuments();
      await rootDispatch.popup.addSavedOkSnackbar();
    },
    async addAutomaticCostDocumentNegativePairs(context, documentId: string) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await addToDocumentNegativeCostDocumentPair(documentId);
      await dispatch.downloadDocuments();
      await rootDispatch.popup.addSavedOkSnackbar();
    },
    async addAutomaticCostDocumentPositivePairs(context, documentId: string) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      await addToDocumentPositiveCostDocumentPair(documentId);
      await dispatch.downloadDocuments();
      await rootDispatch.popup.addSavedOkSnackbar();
    },
  },
});

export default VATMarginSettlementsModule;

/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, VATMarginSettlementsModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, VATMarginSettlementsModule);
